import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import FaqAccordion from '../components/FaqAccordion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import { HERO_MEDIA } from '../config/media';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
} from '../lib/seo';

// Map tile link -> media key. CMS TileItem has no mediaKey slot (and
// shouldn't - media is managed via the Hero Slides tab), so we look up
// imagery locally based on the stable tile.link value.
const LINK_TO_MEDIA_KEY = {
    '/projects/infrastructure': 'cranePad',
    '/projects/oil-gas-pipeline': 'pipelineConstruction',
    '/projects/renewables': 'windFarmConstruction',
};

// Frozen inline fallbacks - render identically pre vs post CMS wiring.
const PROJECTS_FALLBACK = {
    hero: {
        eyebrow: 'Projects Portfolio',
        headline: "Ground protection that holds across every Western Canadian project type.",
        body: "From oil & gas pipelines through muskeg to wind farm crane lifts on remote ridges, Centurion access, rig, and crane mats have been engineered into Western Canada's hardest projects since 1955. Multiple facilities, three generations of family ownership, one standard.",
        image_url: HERO_MEDIA.aerialCorridor.src,
    },
    tiles: {
        items: [
            {
                eyebrow: 'Critical Infrastructure',
                title: 'Infrastructure',
                body: 'Pipeline, power & transmission, wind, oil & gas, and utility corridors - engineered access and crane mats for the projects that move Western Canada.',
                link: '/projects/infrastructure',
                image_url: '',
            },
            {
                eyebrow: 'Upstream & Midstream',
                title: 'Oil & Gas / Pipeline',
                body: 'Well-site access, mainline construction, tank-farm staging - 477 mats per kilometre, 25 per corner. Coastal Hem-Fir, 12\u2033 interlocking pockets.',
                link: '/projects/oil-gas-pipeline',
                image_url: '',
            },
            {
                eyebrow: 'Wind \u2022 Solar \u2022 T&D',
                title: 'Wind & Renewables',
                body: 'Engineered crane pads for nacelle lifts, access roads to remote ridges, transmission line and solar tracker pads - custom timber crane mats built to spec.',
                link: '/projects/renewables',
                image_url: '',
            },
        ],
    },
};

// Not currently backed by the CMS (stats/lifecycle/applications/FAQ don't
// have Pydantic shapes). Frozen inline per Phase 2.5 scope rules.
const STATS = [
    { k: 'Since', v: '1955', sub: 'Three generations, family-owned.' },
    { k: 'Locations', v: 'BC + AB', sub: 'Chemainus, BC mill \u2022 Calgary, AB office.' },
    { k: 'Material', v: 'Hi-Line #2', sub: 'Coastal Hem-Fir structural grade.' },
    { k: 'Channel', v: 'Direct', sub: 'Manufacturer Direct, no broker margin.' },
];

const LIFECYCLE = [
    { k: '01', t: 'Spec', body: 'Talk to mill engineering. Field calculator, CAD, custom build-to-spec.' },
    { k: '02', t: 'Manufacture', body: 'Coastal Hem-Fir Hi-Line #2, milled and bolted at our Chemainus, BC facility.' },
    { k: '03', t: 'Deliver', body: 'Highway haul to BC, AB, SK, MB, YT, NT and beyond - full loads, mill-direct dispatch.' },
    { k: '04', t: 'Recover', body: 'Multi-deployment lifecycle - repair, redeploy, repurpose, recover.' },
];

const APPLICATIONS = [
    'Access roads & well-site access',
    'Pipeline crossings & rights-of-way',
    'Tank farms & staging yards',
    'Drilling pads & frac spreads',
    'Wind farm crane lifts',
    'Transmission line corridors',
    'Solar facility staging',
    'Helicopter pads',
    'Heavy crane outrigger pads',
    'Tower crane base support',
    'Emergency egress & remediation',
    'First Nations partnership corridors',
];

const FAQS = [
    {
        q: 'What types of projects does Centurion supply matting for?',
        a: 'Centurion supplies engineered timber matting for oil & gas pipeline, drilling, frac, well-pad and tank-farm work; power and transmission line construction; wind farm and solar facility builds; civil and utility infrastructure; and First Nations partnership projects across Western Canada.',
    },
    {
        q: 'Where in Western Canada does Centurion deliver?',
        a: 'British Columbia, Alberta, Saskatchewan, Manitoba, Yukon, Northwest Territories, and beyond - dispatched Manufacturer Direct from our Chemainus, BC mill, coordinated through our Calgary, AB sales office. Out-of-region deliveries quoted on request.',
    },
    {
        q: 'How do I get a project-specific quote?',
        a: 'Use the Field Calculator to generate an estimate, then submit the project details for a Manufacturer Direct quote - our sales desk responds within one business day with pricing, lead times, and delivery confirmation.',
    },
];

export default function AllProjects() {
    const content = usePageContent('projects.all', PROJECTS_FALLBACK);
    const hero = content.hero || PROJECTS_FALLBACK.hero;
    const tiles = (content.tiles?.items?.length ? content.tiles.items : PROJECTS_FALLBACK.tiles.items);

    return (
        <>
            <SEO
                title="Projects Portfolio - Pipeline, Wind, Infrastructure"
                description="Centurion timber matting on oil & gas pipeline, wind farm, transmission, and infrastructure projects across BC, AB, SK, MB, YT, NT and beyond - Manufacturer Direct since 1955."
                cmsSlug="seo.projects.all"
                path="/projects"
                image={HERO_MEDIA.aerialCorridor.src}
                jsonLd={[
                    orgJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Projects', path: '/projects' },
                    ]),
                    faqJsonLd(FAQS),
                    {
                        '@context': 'https://schema.org',
                        '@type': 'CollectionPage',
                        name: 'Centurion Project Categories',
                        url: 'https://centurionmatting.com/projects',
                        hasPart: tiles.map((c) => ({
                            '@type': 'WebPage',
                            name: c.title,
                            url: `https://centurionmatting.com${c.link}`,
                        })),
                    },
                ]}
            />

            <HeroPlaceholder
                mediaKey="aerialCorridor"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            {/* Project categories */}
            <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
                <span className="eyebrow">Project Categories</span>
                <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                    Three project hubs, one engineering standard.
                </h2>
                <div className="mt-12 grid gap-px bg-brand-hairline md:grid-cols-3" data-testid="project-categories">
                    {tiles.map((c) => {
                        const mediaKey = LINK_TO_MEDIA_KEY[c.link];
                        const imageSrc = c.image_url || (mediaKey ? HERO_MEDIA[mediaKey].src : '');
                        return (
                            <Link
                                key={c.link}
                                to={c.link}
                                data-testid={`category-${c.link.replace(/\//g, '-')}`}
                                className="group relative flex flex-col overflow-hidden bg-brand-surface transition-transform duration-200 hover:-translate-y-1"
                            >
                                <div
                                    className="relative h-44 bg-cover bg-center md:h-56"
                                    style={{
                                        backgroundImage: `linear-gradient(180deg, rgba(11,11,13,0.25), rgba(11,11,13,0.85)), url(${imageSrc})`,
                                    }}
                                />
                                <div className="flex flex-1 flex-col gap-3 p-6 md:p-8">
                                    <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-brand-gold">
                                        {c.eyebrow}
                                    </span>
                                    <h3 className="font-display text-xl font-bold uppercase tracking-tight text-brand-text">
                                        {c.title}
                                    </h3>
                                    <p className="font-sans text-sm leading-relaxed text-brand-muted">
                                        {c.body}
                                    </p>
                                    <span className="mt-3 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.18em] text-brand-gold">
                                        Explore
                                        <ArrowRight
                                            size={12}
                                            className="transition-transform duration-150 group-hover:translate-x-1"
                                        />
                                    </span>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* By the numbers */}
            <section className="border-y border-brand-hairline bg-brand-surface" data-eyebrow-tone="blue">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                    <span className="eyebrow">By the Numbers</span>
                    <div className="mt-10 grid gap-px bg-brand-hairline md:grid-cols-4" data-testid="projects-stats">
                        {STATS.map((s) => (
                            <div key={s.k} className="bg-brand-surface p-6 md:p-8">
                                <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-brand-gold">
                                    {s.k}
                                </span>
                                <p className="mt-3 font-display text-3xl font-bold tabular-nums text-brand-text">
                                    {s.v}
                                </p>
                                <p className="mt-3 font-sans text-sm leading-relaxed text-brand-muted">
                                    {s.sub}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Project lifecycle */}
            <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
                <span className="eyebrow">Project Lifecycle</span>
                <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                    Spec → Manufacture → Deliver → Recover.
                </h2>
                <div className="mt-12 grid gap-px bg-brand-hairline md:grid-cols-4" data-testid="lifecycle">
                    {LIFECYCLE.map((s) => (
                        <div key={s.k} className="bg-brand-base p-6 md:p-8">
                            <span className="font-display text-xs font-bold tabular-nums tracking-[0.2em] text-brand-gold">
                                {s.k}
                            </span>
                            <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-tight text-brand-text">
                                {s.t}
                            </h3>
                            <p className="mt-3 font-sans text-sm leading-relaxed text-brand-muted">
                                {s.body}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured applications */}
            <section className="border-y border-brand-hairline bg-brand-surface">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                    <span className="eyebrow">Featured Applications</span>
                    <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                        Twelve places Centurion mats earn their keep.
                    </h2>
                    <ul
                        className="mt-12 grid gap-px bg-brand-hairline sm:grid-cols-2 lg:grid-cols-3"
                        data-testid="applications"
                    >
                        {APPLICATIONS.map((a, i) => (
                            <li
                                key={a}
                                className="flex items-center gap-4 bg-brand-surface p-5 md:p-6"
                            >
                                <span className="font-display text-[10px] font-bold tabular-nums tracking-[0.2em] text-brand-gold">
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <span className="font-sans text-sm text-brand-text">{a}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* FAQ */}
            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">FAQ</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Common questions about Centurion projects.
                </h2>
                <div className="mt-10">
                    <FaqAccordion faqs={FAQS} testId="faq-projects" />
                </div>
            </section>

            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Spec your project. We'll quote within a business day."
                body="Pipeline, wind, infrastructure - Manufacturer Direct from our Chemainus, BC mill."
                cta={{ to: '/quote', label: 'Get a Manufacturer Direct Quote' }}
                secondary={{ to: '/contact', label: 'Talk to Sales' }}
            />
        </>
    );
}
